/**
 * Impersonation session handling (no React).
 *
 * When a super-admin impersonates a user we back up the real admin session into separate
 * `admin_*` localStorage keys, then overwrite the live `auth_*` keys with the short-lived
 * impersonation token. The impersonation token has no refresh token, so when it expires the
 * httpClient restore guard swaps the admin session back. Shared by the impersonation store,
 * the banner and the httpClient refresh-failure path.
 */

const TOKEN_KEY = 'auth_token';
const REFRESH_KEY = 'auth_refresh_token';
const USER_KEY = 'auth_user';

const ADMIN_TOKEN_KEY = 'admin_token';
const ADMIN_REFRESH_KEY = 'admin_refresh_token';
const ADMIN_USER_KEY = 'admin_user';
const IMPERSONATION_FLAG = 'impersonation_active';

export interface ImpersonatedUser {
  id: string;
  email: string;
}

export function isImpersonating(): boolean {
  return localStorage.getItem(IMPERSONATION_FLAG) === 'true';
}

export function impersonatedEmail(): string {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) {
    return '';
  }
  try {
    const parsed = JSON.parse(userStr) as { email?: string };
    return typeof parsed.email === 'string' ? parsed.email : '';
  } catch {
    return '';
  }
}

export function startImpersonation(token: string, user: ImpersonatedUser): void {
  // Back up the real admin session FIRST so a crash mid-swap never strips it.
  const adminToken = localStorage.getItem(TOKEN_KEY);
  const adminRefresh = localStorage.getItem(REFRESH_KEY);
  const adminUser = localStorage.getItem(USER_KEY);
  if (adminToken) {
    localStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
  }
  if (adminRefresh) {
    localStorage.setItem(ADMIN_REFRESH_KEY, adminRefresh);
  }
  if (adminUser) {
    localStorage.setItem(ADMIN_USER_KEY, adminUser);
  }

  localStorage.setItem(IMPERSONATION_FLAG, 'true');
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

// Restores the backed-up admin session over the live keys. Returns true when a backup
// existed and was restored, false otherwise. Idempotent.
export function restoreAdminSession(): boolean {
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (!adminToken) {
    localStorage.removeItem(IMPERSONATION_FLAG);
    return false;
  }

  const adminRefresh = localStorage.getItem(ADMIN_REFRESH_KEY);
  const adminUser = localStorage.getItem(ADMIN_USER_KEY);

  localStorage.setItem(TOKEN_KEY, adminToken);
  if (adminRefresh) {
    localStorage.setItem(REFRESH_KEY, adminRefresh);
  } else {
    localStorage.removeItem(REFRESH_KEY);
  }
  if (adminUser) {
    localStorage.setItem(USER_KEY, adminUser);
  } else {
    localStorage.removeItem(USER_KEY);
  }

  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_REFRESH_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
  localStorage.removeItem(IMPERSONATION_FLAG);
  return true;
}
